import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  LinearProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { Download, InsertDriveFile, Description, PictureAsPdf, TextSnippet, FolderZip, Error } from '@mui/icons-material';
import { downloadMeetingArtifact } from '../../services/meetingService';

const formatBytes = (bytes) => {
  if (bytes == null) return 'Size unknown';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const typeIcon = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'pdf': return <PictureAsPdf />;
    case 'text':
    case 'txt': return <TextSnippet />;
    case 'zip': return <FolderZip />;
    case 'docx':
    case 'doc': return <Description />;
    default: return <InsertDriveFile />;
  }
};

/**
 * Authenticated artifact download list. Downloads go through the server-side
 * storage proxy; the client never receives storage URLs.
 */
const ArtifactDownloads = ({ meetingId, artifacts }) => {
  const [downloading, setDownloading] = useState({});
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState({});

  if (!artifacts || artifacts.length === 0) return null;

  const handleDownload = async (artifact) => {
    setErrors((prev) => ({ ...prev, [artifact.id]: null }));
    setDownloading((prev) => ({ ...prev, [artifact.id]: true }));
    setProgress((prev) => ({ ...prev, [artifact.id]: 0 }));
    try {
      const response = await downloadMeetingArtifact(meetingId, artifact.id, {
        onDownloadProgress: (event) => {
          if (event.total) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setProgress((prev) => ({ ...prev, [artifact.id]: percent }));
          }
        }
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = artifact.name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to download artifact';
      setErrors((prev) => ({ ...prev, [artifact.id]: message }));
    } finally {
      setDownloading((prev) => ({ ...prev, [artifact.id]: false }));
    }
  };

  return (
    <Card data-testid="artifact-downloads">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Artifacts
        </Typography>
        <List dense disablePadding>
          {artifacts.map((artifact) => (
            <Box key={artifact.id}>
              <ListItem
                divider
                disableGutters
                secondaryAction={
                  <Tooltip title="Download">
                    <IconButton
                      edge="end"
                      color="primary"
                      size="small"
                      onClick={() => handleDownload(artifact)}
                      disabled={downloading[artifact.id]}
                      aria-label={`Download ${artifact.name}`}
                    >
                      <Download />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemIcon>{typeIcon(artifact.type)}</ListItemIcon>
                <ListItemText
                  primary={artifact.name}
                  secondary={formatBytes(artifact.sizeBytes)}
                />
              </ListItem>
              {downloading[artifact.id] && (
                <Box sx={{ px: 2, pb: 1 }}>
                  <LinearProgress
                    variant={progress[artifact.id] ? 'determinate' : 'indeterminate'}
                    value={progress[artifact.id] || 0}
                    data-testid={`progress-${artifact.id}`}
                  />
                </Box>
              )}
              {errors[artifact.id] && (
                <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setErrors((prev) => ({ ...prev, [artifact.id]: null }))}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Error fontSize="small" />
                    {errors[artifact.id]}
                  </Box>
                </Alert>
              )}
            </Box>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default ArtifactDownloads;